import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAdminLanguage } from "../contexts/AdminLanguageContext";
import { LayoutDashboard, UserPlus, Building2, FolderKanban, Receipt, Headphones, LogOut, Loader2, Menu, FileText, Package, ClipboardList, BookOpen, Send, Sparkles, Globe } from "lucide-react";
import { useState } from "react";

const navItems = [
  { to: "/admin/dashboard", labelKey: "nav_dashboard", icon: LayoutDashboard },
  { to: "/admin/leads", labelKey: "nav_leads", icon: UserPlus },
  { to: "/admin/customers", labelKey: "nav_customers", icon: Building2 },
  { to: "/admin/projects", labelKey: "nav_projects", icon: FolderKanban },
  { to: "/admin/articles", labelKey: "nav_articles", icon: Package },
  { to: "/admin/invoices", labelKey: "nav_invoices", icon: Receipt },
  { to: "/admin/agreements", labelKey: "nav_agreements", icon: FileText },
  { to: "/admin/onboarding", labelKey: "nav_onboarding", icon: ClipboardList },
  { to: "/admin/outreach", labelKey: "nav_outreach", icon: Send },
  { to: "/admin/prompts", labelKey: "nav_prompts", icon: Sparkles },
  { to: "/admin/bookkeeping", labelKey: "nav_bookkeeping", icon: BookOpen },
  { to: "/admin/support", labelKey: "nav_support", icon: Headphones },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const { lang, setLang, langs, t } = useAdminLanguage();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) return <div className="min-h-screen bg-[#0A1628] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#F59073] animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role === "accountant") return <Navigate to="/admin/accountant/bookkeeping" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b border-[#1E3050]">
        <a href="/" className="font-outfit font-medium text-[#F3E9D6] text-lg tracking-tight">wedowedo</a>
        <p className="font-mono text-[10px] tracking-[0.2em] text-[#857973] mt-1">CRM SYSTEM</p>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(({ to, labelKey, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setSidebarOpen(false)}
            data-testid={`nav-${to.split("/").pop()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                isActive ? "bg-[#F59073]/10 text-[#F59073]" : "text-[#857973] hover:text-[#F3E9D6] hover:bg-[#1E3050]/50"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            {t(labelKey)}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-[#1E3050]">
        <div className="text-xs text-[#857973] font-manrope mb-3 px-3">{user?.name || user?.email}</div>
        <button
          data-testid="admin-logout-btn"
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-[#857973] hover:text-[#A83B24] hover:bg-[#A83B24]/5 transition-colors w-full"
        >
          <LogOut className="w-4 h-4" /> {t("nav_logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="admin-theme min-h-screen bg-[#0A1628] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 flex-col bg-[#0F1F3A] border-r border-[#1E3050] fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-[#0F1F3A] border-r border-[#1E3050] flex flex-col z-50">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-60 min-w-0">
        <header className="sticky top-0 z-20 bg-[#0A1628]/80 backdrop-blur-xl border-b border-[#1E3050] h-14 flex items-center px-6 gap-4">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-[#F3E9D6]"><Menu className="w-5 h-5" /></button>
          <div className="flex-1" />
          <div className="flex items-center gap-1 bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-0.5" data-testid="admin-lang-switcher">
            <Globe className="w-3.5 h-3.5 text-[#857973] mx-1.5" />
            {langs.map(l => (
              <button key={l.code} onClick={() => setLang(l.code)} data-testid={`admin-lang-${l.code}`}
                className={`px-2 py-1 rounded-sm text-[10px] font-mono tracking-wider transition-colors ${lang === l.code ? "bg-[#F59073] text-black" : "text-[#857973] hover:text-white"}`}
                title={l.name}>
                {l.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-[#857973] font-mono tracking-wider">{user?.role?.toUpperCase()}</span>
        </header>
        <main className="p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
